import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Receipt } from '@/components/receipts/Receipt'
import { authFetch } from '@/lib/api'
import {
  fromShopStateDone,
  trackTierTap,
  type ReceiptVM,
} from '@/lib/receipts'
import { formatRelative } from '@/lib/dates'
import { formatPhone } from '@/lib/format'
import {
  SHOP_STATE_QUERY_KEY,
  isExecutableAction,
  useShopState,
  type ExecutableShopStateAction,
  type ShopStateAction,
} from '@/lib/shop-state'

/**
 * The needs-you stack (DESIGN.md §4.2, plan PR-5): at most three amber
 * cards from the shop-state resolver's `rankedActions`, each committing
 * its pre-bound `execute` descriptor exactly as NextBestActions.run()
 * does ("propose, human disposes" — architecture §5; actions may
 * supersede underfoot). A resolved card leaves the stack and its
 * outcome renders as a §4.1 receipt in place. Amber vocabulary only —
 * no side-stripes, no urgency theater (§2.4).
 */

/** §4.2: max 3 on Today. Overflow renders as a quiet count row — the
    tap-through to Activity lands with PR-7 when the route exists. */
const MAX_CARDS = 3
/** Parity with the dock's ReceiptsHost cap (§4.3). */
const MAX_RECEIPTS = 2
const UNDONE_LINGER_MS = 4000

// kind → i18n suffix (running_long + nearing_end share the Complete verb).
// review_inbound_text never renders a button (execute is null; the card is
// informational) — its entry exists only to keep the Record exhaustive.
const KIND_SUFFIX: Record<ShopStateAction['kind'], string> = {
  mark_arrived: 'mark_arrived',
  mark_no_show: 'mark_no_show',
  complete_appointment: 'complete',
  review_inbound_text: 'review_inbound_text',
}

function targetText(a: ShopStateAction, fallback: string): string {
  if (a.target.type === 'inbound_text') {
    return a.target.customerName ?? formatPhone(a.target.fromE164)
  }
  return (
    [a.target.customerName, a.target.vehicleLabel]
      .filter(Boolean)
      .join(' · ') || fallback
  )
}

export function NeedsYouStack() {
  const { t } = useTranslation('today')
  const { t: tReceipts } = useTranslation('receipts')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const { data } = useShopState()

  const [pendingId, setPendingId] = useState<string | null>(null)
  const [errorId, setErrorId] = useState<string | null>(null)
  // Acted cards leave the stack immediately (§4.2) — the shop-state
  // refetch confirms server-side a beat later.
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<string>>(
    new Set(),
  )
  // Each receipt remembers which action produced it so an undo can
  // un-resolve exactly that card.
  const [receipts, setReceipts] = useState<
    { vm: ReceiptVM; actionId: string }[]
  >([])

  function invalidateAll() {
    // Same query set NextBestActions invalidates after a status flip —
    // keeps the resolver, load and needs-action widgets coherent.
    queryClient.invalidateQueries({ queryKey: SHOP_STATE_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
    queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
    queryClient.invalidateQueries({ queryKey: ['agentic-saved', 'today'] })
  }

  async function run(action: ExecutableShopStateAction) {
    if (pendingId) return
    setErrorId(null)
    setPendingId(action.id)
    try {
      const res = await authFetch<{ ok: boolean; request_id: string | null }>(
        action.execute.path,
        getToken,
        {
          method: action.execute.method,
          body: JSON.stringify(action.execute.body),
        },
      )
      trackTierTap({ verb: action.kind, tier: 'chip', surface: 'today' })
      const vm = fromShopStateDone(
        action,
        res.request_id,
        tReceipts,
        new Date().toISOString(),
      )
      setResolvedIds((prev) => new Set(prev).add(action.id))
      setReceipts((prev) =>
        [{ vm, actionId: action.id }, ...prev].slice(0, MAX_RECEIPTS),
      )
      invalidateAll()
    } catch {
      setErrorId(action.id)
    } finally {
      setPendingId(null)
    }
  }

  // An undo re-opens the decision: un-resolve the acted card right away
  // (the re-resolve may put it back while the struck receipt lingers),
  // then drop the receipt after the linger.
  const undoneTimers = useRef<number[]>([])
  const handleUndone = (vm: ReceiptVM) => {
    const acted = receipts.find((r) => r.vm.requestId === vm.requestId)
    if (acted) {
      setResolvedIds((prev) => {
        const next = new Set(prev)
        next.delete(acted.actionId)
        return next
      })
    }
    invalidateAll()
    const id = window.setTimeout(() => {
      setReceipts((prev) =>
        prev.filter((r) => r.vm.requestId !== vm.requestId),
      )
    }, UNDONE_LINGER_MS)
    undoneTimers.current.push(id)
  }
  useEffect(() => {
    const timers = undoneTimers.current
    return () => timers.forEach((id) => window.clearTimeout(id))
  }, [])

  const ranked = data?.rankedActions ?? []
  const live = ranked.filter((a) => !resolvedIds.has(a.id))
  const cards = live.slice(0, MAX_CARDS)
  const overflow = live.length - cards.length

  if (cards.length === 0 && receipts.length === 0) return null

  return (
    <section aria-label={t('needs_you.label')} className="space-y-2">
      <h2 className="text-[13px] font-semibold text-muted-foreground">
        {t('needs_you.label')}
      </h2>

      {receipts.length > 0 ? (
        <div className="space-y-2">
          {receipts.map(({ vm }) => (
            <Receipt
              key={vm.requestId ?? vm.createdAtIso}
              vm={vm}
              surface="today"
              onUndone={handleUndone}
            />
          ))}
        </div>
      ) : null}

      <ul className="space-y-2">
        {cards.map((a) => {
          // Only appointment actions carry an execute today; inbound_text
          // cards are informational (no button).
          const executable = isExecutableAction(a) ? a : null
          return (
            <li
              key={a.id}
              className="rounded-[14px] border border-attention-line bg-attention-soft px-3.5 py-[13px]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-semibold text-foreground">
                    {targetText(a, t('needs_you.fallback_target'))}
                  </div>
                  <div className="truncate text-[13px] font-medium text-attention-text">
                    {t(`needs_you.reflex.${a.reflex}`)}
                    {a.target.type === 'inbound_text'
                      ? ` · ${formatRelative(a.target.receivedAtIso)}`
                      : null}
                  </div>
                </div>
                {executable ? (
                  <button
                    type="button"
                    disabled={pendingId !== null}
                    onClick={() => run(executable)}
                    className="min-h-11 shrink-0 rounded-full bg-foreground px-4 text-sm font-semibold text-background transition-transform [transition-duration:120ms] active:scale-[0.97] disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-foreground/90"
                  >
                    {pendingId === a.id
                      ? t('needs_you.working')
                      : t(`needs_you.action.${KIND_SUFFIX[a.kind]}`)}
                  </button>
                ) : null}
              </div>
              {a.target.type === 'inbound_text' && a.target.preview ? (
                <p className="mt-1.5 line-clamp-2 text-[13px] text-muted-foreground">
                  {a.target.preview}
                </p>
              ) : null}
              {errorId === a.id ? (
                <div className="mt-1.5 text-[13px] font-medium text-destructive">
                  {t('needs_you.error')}
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>

      {overflow > 0 ? (
        <p className="text-[13px] text-muted-foreground">
          {t('needs_you.more_waiting', { count: overflow })}
        </p>
      ) : null}
    </section>
  )
}
